import { useState } from 'react'
import { NumPad } from './NumPad'
import type { ReactNode } from 'react'
import { I } from './Icons'
import { Sheet, useSheetClose } from './Sheet'

export function Seg<T extends string>({ value, options, onChange, disabled }: { value: T; options: { v: T; label: string; off?: boolean }[]; onChange: (v: T) => void; disabled?: boolean }) {
  return (
    <div className="seg" role="group">
      {options.map((o) => (
        <button key={o.v} type="button" className="seg__btn" aria-pressed={value === o.v} disabled={disabled || o.off} onClick={() => onChange(o.v)}>{o.label}</button>
      ))}
    </div>
  )
}

export function SegRow<T extends string>(p: { label: string } & Parameters<typeof Seg<T>>[0]) {
  return <div className="row"><span className="row__label">{p.label}</span><Seg {...p} /></div>
}

export function InputRow({ id, label, value, onChange, placeholder, numeric, left, tel, email, caps }: { id: string; label: string; value: string; onChange: (v: string) => void; placeholder?: string; numeric?: boolean; left?: boolean; tel?: boolean; email?: boolean; caps?: boolean }) {
  return (
    <div className={'row' + (left ? ' fieldrow' : '')}>
      <label htmlFor={id} className="row__label">{label}</label>
      <input id={id} className="row__input" type={tel ? 'tel' : 'text'} inputMode={numeric ? 'numeric' : tel ? 'tel' : email ? 'email' : undefined} value={value} placeholder={placeholder}
        onChange={(e) => onChange(numeric ? e.target.value.replace(/[^0-9]/g, '') : e.target.value)} autoComplete="off" autoCapitalize={numeric || tel || email ? 'off' : caps ? 'characters' : 'words'} style={caps ? { textTransform: 'uppercase' } : undefined} autoCorrect={email ? 'off' : undefined} />
    </div>
  )
}

export function NavRow({ label, value, set, onClick }: { label: string; value: string; set?: boolean; onClick: () => void }) {
  return (
    <button type="button" className="row row--nav" onClick={onClick}>
      <span className="row__label">{label}</span><span className={'row__value' + (set ? ' set' : '')}>{value}</span>{I.chev()}
    </button>
  )
}

export function KV({ k, v, bold }: { k: string; v: ReactNode; bold?: boolean }) {
  return <div className="row"><span className="row__label">{k}</span><span className="row__value" style={bold ? { fontWeight: 600 } : undefined}>{v}</span></div>
}


/** A number field that opens the app's own keypad instead of the iPad keyboard (which has no plain numeric layout). */
export function MoneyRow({ id, label, value, onChange, placeholder, prefix = '$', suffix = '', decimal = false, max = 7, hint, openOnMount = false }: { id: string; label: string; value: string; onChange: (v: string) => void; placeholder?: string; prefix?: string; suffix?: string; decimal?: boolean; max?: number; hint?: string; openOnMount?: boolean }) {
  const [open, setOpen] = useState(openOnMount)
  const shown = value ? `${prefix}${decimal ? value : Number(value).toLocaleString('en-US')}${suffix}` : ''
  return (
    <>
      <button type="button" id={id} className="row row--nav" onClick={() => setOpen(true)}>
        <span className="row__label">{label}</span>
        <span className={'row__value' + (value ? ' set' : '')}>{shown || placeholder || ''}</span>
      </button>
      {open && (
        <Sheet onClose={() => setOpen(false)} label={label}>
          <div className="sheet__title">{label}</div>
          {hint && <div className="sheet__sub" style={{ marginTop: 4 }}>{hint}</div>}
          <div className="pricepad__value" style={{ margin: '12px 0 14px', textAlign: 'center' }}>{shown || <span style={{ color: 'var(--hairline)' }}>{prefix}0{suffix}</span>}</div>
          <NumPad value={value} onChange={onChange} max={max} decimal={decimal} />
          <SheetDone />
        </Sheet>
      )}
    </>
  )
}


/** Done inside a Sheet: closes with the slide-down. */
function SheetDone() {
  const close = useSheetClose()
  return <button type="button" className="cta cta--center sheet__cta" onClick={close}>Done</button>
}

/** A row with a native dropdown on the right (iPad shows its picker wheel). */
export function SelectRow({ id, label, value, options, onChange, placeholder = 'Choose' }: { id: string; label: string; value: string; options: { v: string; label: string }[]; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div className="row">
      <label htmlFor={id} className="row__label">{label}</label>
      <select id={id} className="row__select" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="" disabled>{placeholder}</option>
        {options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
      </select>
    </div>
  )
}


/** Two taps: the coin, then (for stablecoins) the network. Bitcoin has one network. */
export function CoinNetworkRows({ coin, network, onChange, compact }: { coin: string; network: string; onChange: (coin: string, network: string) => void; compact?: boolean }) {
  const coins = [{ v: 'BTC', label: 'Bitcoin' }, { v: 'USDC', label: 'USDC' }, { v: 'USDT', label: 'USDT' }]
  const nets = [{ v: 'Ethereum', label: 'Ethereum' }, { v: 'Solana', label: 'Solana' }, { v: 'Tron', label: 'Tron' }]
  const pickCoin = (c: string) => onChange(c, c === 'BTC' ? 'Bitcoin' : (network === 'Bitcoin' ? '' : network))
  return (
    <>
      {compact ? <Seg value={coin} options={coins} onChange={pickCoin} /> : <SegRow label="Coin" value={coin} options={coins} onChange={pickCoin} />}
      {(coin === 'USDC' || coin === 'USDT') && (compact
        ? <div style={{ marginTop: 8 }}><Seg value={network} options={nets} onChange={(n) => onChange(coin, n)} /></div>
        : <SegRow label="Network" value={network} options={nets} onChange={(n) => onChange(coin, n)} />)}
    </>
  )
}
